import React from "react";
import PropTypes from "prop-types";
import {useT} from "../i18n/i18n";

export default function About({language}) {
    const {t, tArray} = useT(language);
    const paragraphs = tArray("about.description");
    const howIWorkSteps = tArray("howIWork.steps");

    return (
        <>
            {/* About Section */}
            <section className="w-full py-12 sm:py-16 overflow-hidden">
                <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12 items-center">
                    {/* Portrait Image - Left Side (Mobile: Full Width, Desktop: 1 Col) */}
                    <div className="lg:col-span-1 w-full min-w-0 lg:pt-8">
                        <div className="w-full max-w-xs mx-auto lg:mx-0">
                            <div className="relative rounded-2xl overflow-hidden shadow-lg bg-[rgb(var(--app-card))] border border-[rgb(var(--app-border))]">
                                <img
                                    src="/chisonoportrait.jpg"
                                    alt={language === "en" ? "Yurii Suprun Portrait" : "Ritratto di Yurii Suprun"}
                                    className="w-full h-auto object-cover aspect-[3/4]"
                                    loading="lazy"
                                />
                                {/* Subtle overlay for better text readability if needed */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                            </div>
                        </div>
                    </div>

                    {/* Text Content - Right Side (Mobile: Full Width, Desktop: 2 Cols) */}
                    <div className="lg:col-span-2 w-full min-w-0 px-4 sm:px-0">
                        <h2 className="text-2xl sm:text-3xl font-extrabold mb-6 sm:mb-8 leading-tight text-black">{t("about.title")}</h2>
                        <div className="space-y-5 sm:space-y-6">
                            {paragraphs.map((paragraph, idx) => (
                                <p key={idx} className="text-black/80 leading-relaxed text-base sm:text-base break-words max-w-2xl">{paragraph}</p>
                            ))}
                        </div>

                        {/* CTA */}
                        <div className="mt-8 sm:mt-10">
                            <a href="/#contacts" className="inline-flex items-center gap-2 text-[#E19F3B] font-semibold hover:opacity-80 transition-opacity">
                                {language === "en" ? "Let's work together" : "Lavoriamo insieme"} →
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* How I Work Section */}
            <section className="py-8 sm:py-12 w-full bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    {/* Header */}
                    <div className="mb-14 text-center sm:text-left">
                        <p className="text-xs font-semibold uppercase tracking-widest text-[#E19F3B] mb-3">
                            {language === "it" ? "IL MIO APPROCCIO" : "MY APPROACH"}
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-bold mb-4 leading-tight text-black">
                            {t("howIWork.title")}
                        </h2>
                        <p className="text-base text-black/70 leading-relaxed max-w-2xl">
                            {t("howIWork.subtitle")}
                        </p>
                    </div>

                    {/* How I Work Steps - Responsive Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {howIWorkSteps && howIWorkSteps.map((step, idx) => (
                            <div 
                                key={idx} 
                                className="flex flex-col p-6 rounded-xl border border-gray-300 hover:shadow-md hover:border-gray-400 transition-all duration-300 bg-white"
                            >
                                {/* Step Number Badge */}
                                <div className="mb-4">
                                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#C9860F] text-white font-bold text-sm shadow-md">
                                        {step.number}
                                    </span>
                                </div>

                                {/* Step Title */}
                                <h3 className="text-sm font-semibold mb-3 text-black leading-snug">
                                    {step.title}
                                </h3>

                                {/* Step Description */}
                                <p className="text-sm text-black/70 leading-relaxed flex-grow">
                                    {step.description}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* CTA */}
                    <div className="mt-8 text-center">
                        <a 
                            href="/#contacts"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-[#E19F3B] text-white font-semibold text-sm rounded-lg hover:bg-[#D68A2B] transition-colors duration-200"
                        >
                            {language === "it" ? "Inizia il Tuo Progetto" : "Start Your Project"}
                        </a>
                    </div>
                </div>
            </section>
        </>
    );
}

About.propTypes = {
    language: PropTypes.oneOf(["en", "it"]),
};
